import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useApi, useConsulta } from '@plataforma/web/api';
import { fechaCorta, mxn } from '../formato';
import { usePuede } from '../permisos';
import type { Vehiculo } from './tipos';

interface OrdenTablero {
  ordenId: string;
  vehiculoId: string;
  vin: string;
  tipo: 'reparacion' | 'preparacion';
  estado: string;
  diasEnTaller: number;
  avancePct: number;
  costoEstimadoMxn: string;
  costoRealMxn: string;
  desviacionPct: number | null;
  alertaDesviacion: boolean;
}
interface Tarea {
  id: string;
  etapa: string;
  descripcion: string;
  responsable: string | null;
  horasEstimadas: string;
  horasReales: string;
  costoExternoEstimadoMxn: string;
  costoExternoRealMxn: string | null;
  estado: 'pendiente' | 'en_proceso' | 'terminada';
  relojCorriendo: boolean;
}
interface PiezaOrden {
  id: string;
  nombre: string;
  numeroParte: string | null;
  origen: string;
  costoEstimadoMxn: string;
  costoRealMxn: string | null;
  estado: 'pedida' | 'recibida' | 'instalada';
}
interface Orden {
  id: string;
  vehiculoId: string;
  vin: string;
  tipo: 'reparacion' | 'preparacion';
  estado: string;
  tarifaHoraMxn: string;
  inspeccionFinal: string | null;
  creadaEn: string;
  cerradaEn: string | null;
  tareas: Tarea[];
  piezas: PiezaOrden[];
  resumen: {
    avancePct: number;
    costoEstimadoMxn: string;
    costoRealMxn: string;
    desviacionPct: number | null;
    alertaDesviacion: boolean;
  };
}

export const ETAPAS: Record<string, string> = {
  desmontaje: 'Desmontaje',
  hojalateria: 'Hojalatería',
  mecanica: 'Mecánica',
  pintura: 'Pintura',
  armado: 'Armado',
  electrico_diagnostico: 'Eléctrico y diagnóstico',
  pruebas: 'Pruebas',
  detallado: 'Detallado',
  servicio: 'Servicio',
  verificacion: 'Verificación',
  placas_tramites: 'Placas y trámites',
};
const ETAPAS_DE: Record<Orden['tipo'], string[]> = {
  reparacion: [
    'desmontaje',
    'hojalateria',
    'mecanica',
    'pintura',
    'armado',
    'electrico_diagnostico',
    'pruebas',
    'detallado',
  ],
  preparacion: ['detallado', 'servicio', 'verificacion', 'placas_tramites'],
};
const TIPO: Record<Orden['tipo'], string> = {
  reparacion: 'Reparación',
  preparacion: 'Preparación',
};

function Avance({ pct }: { pct: number }) {
  return (
    <div
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      style={{ background: 'var(--borde-suave)', borderRadius: 6, height: 8, minWidth: 80 }}
    >
      <div
        style={{
          width: `${Math.min(100, pct)}%`,
          height: '100%',
          borderRadius: 6,
          background: 'var(--exito, #2e7d32)',
        }}
      />
    </div>
  );
}

/** Tablero del taller (modo «Patio y taller», arquitectura 2.9): órdenes abiertas y su avance. */
export function Taller() {
  const tablero = useConsulta<OrdenTablero[]>('/workshop/tablero');
  const permitido = usePuede();
  const ordenes = tablero.datos ?? [];
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Taller</h1>
          <div className="subtitulo">Órdenes abiertas de reparación y preparación</div>
        </div>
        <div className="relleno" />
        {permitido('ordenes.trabajar') && (
          <Link className="boton primario" to="/taller/nueva">
            Abrir orden
          </Link>
        )}
      </div>
      {tablero.error && <p className="error">{tablero.error}</p>}
      <section className="seccion">
        {tablero.datos && !ordenes.length ? (
          <p className="vacio">
            No hay órdenes abiertas. Se abren aquí o al aprobar una estimación.
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Vehículo</th>
                <th>Tipo</th>
                <th>Días</th>
                <th>Avance</th>
                <th className="num">Costo real / estimado</th>
              </tr>
            </thead>
            <tbody>
              {ordenes.map((o) => (
                <tr key={o.ordenId}>
                  <td>
                    <Link to={`/taller/${o.ordenId}`}>{o.vin}</Link>
                    {o.alertaDesviacion && (
                      <div className="etiqueta aviso">desviación {o.desviacionPct}%</div>
                    )}
                  </td>
                  <td>{TIPO[o.tipo]}</td>
                  <td>{o.diasEnTaller}</td>
                  <td>
                    <Avance pct={o.avancePct} /> <small>{o.avancePct}%</small>
                  </td>
                  <td className="num">
                    {mxn(o.costoRealMxn)} / {mxn(o.costoEstimadoMxn)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

/** Abrir una orden para un vehículo del inventario (sin estimación previa). */
export function NuevaOrden() {
  const api = useApi();
  const navegar = useNavigate();
  const [params] = useSearchParams();
  const vehiculos = useConsulta<Vehiculo[]>('/inventory/vehiculos');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const abrir = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const datos = new FormData(e.currentTarget);
    const v = (vehiculos.datos ?? []).find((x) => x.id === datos.get('vehiculo'));
    if (!v) return setError('Elige un vehículo');
    setEnviando(true);
    setError(null);
    try {
      const orden = await api<Orden>('POST', '/workshop/ordenes', {
        vehiculoId: v.id,
        vin: v.vin,
        tipo: datos.get('tipo'),
        tarifaHoraMxn: datos.get('tarifa'),
      });
      void navegar(`/taller/${orden.id}`);
    } catch (err) {
      setError((err as Error).message);
      setEnviando(false);
    }
  };
  return (
    <>
      <div className="encabezado">
        <div>
          <div className="subtitulo">
            <Link to="/taller">Taller</Link> ›
          </div>
          <h1>Abrir orden</h1>
        </div>
      </div>
      <form className="seccion cuerpo formulario" onSubmit={(e) => void abrir(e)}>
        <label className="campo">
          <span>Vehículo</span>
          <select name="vehiculo" required defaultValue={params.get('vehiculo') ?? ''}>
            <option value="" disabled>
              Elige…
            </option>
            {(vehiculos.datos ?? [])
              .filter((v) => !v.baja)
              .map((v) => (
                <option key={v.id} value={v.id}>
                  {v.vin} · {[v.anio, v.marca, v.modelo].filter(Boolean).join(' ')}
                </option>
              ))}
          </select>
        </label>
        <label className="campo">
          <span>Tipo</span>
          <select name="tipo" defaultValue="reparacion">
            <option value="reparacion">Reparación</option>
            <option value="preparacion">Preparación (seminuevos)</option>
          </select>
        </label>
        <label className="campo">
          <span>Tarifa de mano de obra (MXN por hora)</span>
          <input name="tarifa" required inputMode="decimal" pattern="\d{1,10}(\.\d{1,2})?" />
        </label>
        {error && <p className="error">{error}</p>}
        <div>
          <button className="boton primario" disabled={enviando}>
            {enviando ? 'Abriendo…' : 'Abrir orden'}
          </button>
        </div>
      </form>
    </>
  );
}

/** Orden de taller: tareas por etapa con reloj, piezas, avance y cierre (celular primero). */
export function OrdenTaller() {
  const { id } = useParams();
  const api = useApi();
  const permitido = usePuede();
  const orden = useConsulta<Orden>(`/workshop/ordenes/${id}`);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const trabaja = permitido('ordenes.trabajar');

  const hacer = async (clave: string, metodo: 'POST', ruta: string, cuerpo?: object) => {
    setError(null);
    setOcupado(clave);
    try {
      await api(metodo, ruta, cuerpo);
      orden.recargar();
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setOcupado(null);
    }
  };

  const o = orden.datos;
  if (orden.error) return <p className="error">{orden.error}</p>;
  if (!o) return <p className="subtitulo">Cargando…</p>;
  const abierta = !['cerrada', 'cancelada'].includes(o.estado);

  const agregarTarea = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const forma = e.currentTarget;
    const d = new FormData(forma);
    const ok = await hacer('tarea', 'POST', `/workshop/ordenes/${o.id}/tareas`, {
      etapa: d.get('etapa'),
      descripcion: d.get('descripcion'),
      horasEstimadas: d.get('horas'),
      ...(d.get('responsable') && { responsable: d.get('responsable') }),
    });
    if (ok) forma.reset();
  };
  const agregarPieza = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const forma = e.currentTarget;
    const d = new FormData(forma);
    const ok = await hacer('pieza', 'POST', `/workshop/ordenes/${o.id}/piezas`, {
      nombre: d.get('nombre'),
      origen: d.get('origen'),
      costoEstimadoMxn: d.get('costo'),
      ...(d.get('numeroParte') && { numeroParte: d.get('numeroParte') }),
    });
    if (ok) forma.reset();
  };
  const cerrar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    await hacer('cerrar', 'POST', `/workshop/ordenes/${o.id}/cerrar`, {
      inspeccionFinal: d.get('inspeccion'),
      ...(d.get('materiales') && { materialesRealesMxn: d.get('materiales') }),
    });
  };

  return (
    <>
      <div className="encabezado">
        <div>
          <div className="subtitulo">
            <Link to="/taller">Taller</Link> ›{' '}
            <Link to={`/vehiculos/${o.vehiculoId}`}>{o.vin}</Link>
          </div>
          <h1>
            {TIPO[o.tipo]} · {o.resumen.avancePct}%
          </h1>
          <div className="subtitulo">
            Abierta el {fechaCorta(o.creadaEn)} · tarifa {mxn(o.tarifaHoraMxn)}/h
          </div>
        </div>
        <div className="relleno" />
        <span className="etiqueta">{o.estado.replace('_', ' ')}</span>
      </div>
      {error && <p className="error">{error}</p>}
      <div className="tarjetas">
        <div className="tarjeta">
          <div className="rotulo">Avance</div>
          <div className="cifra">{o.resumen.avancePct}%</div>
          <Avance pct={o.resumen.avancePct} />
        </div>
        <div className="tarjeta">
          <div className="rotulo">Costo real</div>
          <div className="cifra">{mxn(o.resumen.costoRealMxn)}</div>
          <div className="nota">de {mxn(o.resumen.costoEstimadoMxn)} estimado</div>
        </div>
        <div className="tarjeta">
          <div className="rotulo">Desviación</div>
          <div
            className="cifra"
            style={{ color: o.resumen.alertaDesviacion ? 'var(--peligro)' : undefined }}
          >
            {o.resumen.desviacionPct === null || Number(o.resumen.costoRealMxn) === 0
              ? '—'
              : `${o.resumen.desviacionPct}%`}
          </div>
        </div>
      </div>

      <section className="seccion">
        <header>
          <h2>Tareas</h2>
        </header>
        {!o.tareas.length && <p className="vacio">Sin tareas todavía.</p>}
        <table>
          <tbody>
            {o.tareas.map((t) => (
              <tr key={t.id}>
                <td>
                  <strong>{ETAPAS[t.etapa] ?? t.etapa}</strong> · {t.descripcion}
                  <div className="vin">
                    {t.horasReales} de {t.horasEstimadas} h
                    {t.responsable ? ` · ${t.responsable}` : ''}
                  </div>
                </td>
                <td>
                  <span className={`etiqueta ${t.estado === 'terminada' ? 'exito' : ''}`}>
                    {t.relojCorriendo ? '⏱ corriendo' : t.estado.replace('_', ' ')}
                  </span>
                </td>
                <td className="num">
                  {trabaja && abierta && t.estado !== 'terminada' && (
                    <>
                      {t.relojCorriendo ? (
                        <button
                          className="boton"
                          disabled={ocupado !== null}
                          onClick={() =>
                            void hacer(t.id, 'POST', `/workshop/tareas/${t.id}/detener`)
                          }
                        >
                          Detener
                        </button>
                      ) : (
                        <button
                          className="boton primario"
                          disabled={ocupado !== null}
                          onClick={() =>
                            void hacer(t.id, 'POST', `/workshop/tareas/${t.id}/iniciar`)
                          }
                        >
                          Iniciar
                        </button>
                      )}{' '}
                      <button
                        className="boton"
                        disabled={ocupado !== null}
                        onClick={() =>
                          void hacer(t.id, 'POST', `/workshop/tareas/${t.id}/terminar`, {})
                        }
                      >
                        Terminar
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {trabaja && abierta && (
          <form
            className="cuerpo formulario"
            onSubmit={(e) => void agregarTarea(e)}
            style={{ borderTop: '1px solid var(--borde-suave)' }}
          >
            <label className="campo">
              <span>Etapa</span>
              <select name="etapa" required>
                {ETAPAS_DE[o.tipo].map((e) => (
                  <option key={e} value={e}>
                    {ETAPAS[e]}
                  </option>
                ))}
              </select>
            </label>
            <label className="campo">
              <span>Descripción</span>
              <input name="descripcion" required minLength={2} maxLength={300} />
            </label>
            <label className="campo">
              <span>Horas estimadas</span>
              <input name="horas" required inputMode="decimal" pattern="\d{1,4}(\.\d{1,2})?" />
            </label>
            <label className="campo">
              <span>Responsable</span>
              <input name="responsable" maxLength={120} />
            </label>
            <div>
              <button className="boton" disabled={ocupado !== null}>
                Agregar tarea
              </button>
            </div>
          </form>
        )}
      </section>

      <section className="seccion">
        <header>
          <h2>Piezas</h2>
        </header>
        {!o.piezas.length && <p className="vacio">Sin piezas.</p>}
        <table>
          <tbody>
            {o.piezas.map((p) => (
              <tr key={p.id}>
                <td>
                  {p.nombre}
                  <div className="vin">
                    {p.origen}
                    {p.numeroParte ? ` · ${p.numeroParte}` : ''} ·{' '}
                    {mxn(p.costoRealMxn ?? p.costoEstimadoMxn)}
                  </div>
                </td>
                <td>
                  <span className={`etiqueta ${p.estado === 'instalada' ? 'exito' : ''}`}>
                    {p.estado}
                  </span>
                </td>
                <td className="num">
                  {trabaja && abierta && p.estado !== 'instalada' && (
                    <button
                      className="boton"
                      disabled={ocupado !== null}
                      onClick={() =>
                        void hacer(p.id, 'POST', `/workshop/piezas-orden/${p.id}/estado`, {
                          estado: p.estado === 'pedida' ? 'recibida' : 'instalada',
                        })
                      }
                    >
                      {p.estado === 'pedida' ? 'Recibida' : 'Instalada'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {trabaja && abierta && (
          <form
            className="cuerpo formulario"
            onSubmit={(e) => void agregarPieza(e)}
            style={{ borderTop: '1px solid var(--borde-suave)' }}
          >
            <label className="campo">
              <span>Pieza</span>
              <input name="nombre" required minLength={2} maxLength={120} />
            </label>
            <label className="campo">
              <span>Número de parte</span>
              <input name="numeroParte" maxLength={60} />
            </label>
            <label className="campo">
              <span>Origen</span>
              <select name="origen">
                <option value="nacional">Nacional</option>
                <option value="importacion">Importación</option>
              </select>
            </label>
            <label className="campo">
              <span>Costo estimado (MXN)</span>
              <input name="costo" required inputMode="decimal" pattern="\d{1,10}(\.\d{1,2})?" />
            </label>
            <div>
              <button className="boton" disabled={ocupado !== null}>
                Agregar pieza
              </button>
            </div>
          </form>
        )}
      </section>

      {trabaja && abierta && (
        <form className="seccion cuerpo formulario" onSubmit={(e) => void cerrar(e)}>
          <h2>Cerrar orden</h2>
          <label className="campo">
            <span>Inspección final</span>
            <textarea name="inspeccion" required minLength={5} maxLength={2000} rows={3} />
          </label>
          <label className="campo">
            <span>Materiales reales (MXN, opcional)</span>
            <input name="materiales" inputMode="decimal" pattern="\d{1,10}(\.\d{1,2})?" />
          </label>
          <div>
            <button className="boton primario" disabled={ocupado !== null}>
              Cerrar y pasar el costo real al vehículo
            </button>
          </div>
        </form>
      )}
      {o.inspeccionFinal && (
        <section className="seccion">
          <header>
            <h2>Inspección final</h2>
          </header>
          <p className="cuerpo">{o.inspeccionFinal}</p>
        </section>
      )}
    </>
  );
}
